import { ReactFlowProvider, ReactFlowState, useStore } from '@xyflow/react';
import { PropsWithChildren, useState } from 'react';

import { useMeasureDOMElement } from '@/hooks/useMeasureDOMElement';

import { LiveEdgeCrossingsProvider } from '../edges/LiveEdgeCrossings';
import { FlowChartContext } from './flowChart.context';

const domElSelector = (s: ReactFlowState) => s.domNode;

type EditingProps = Pick<FlowChartContext, 'editable'> & {
  allowGrouping?: boolean;
};

function useFlowChartContextValue({
  editable,
  allowGrouping = true,
}: EditingProps): FlowChartContext {
  const [isDirty, setIsDirty] = useState(false);
  const [markers, setMarkers] = useState({});
  const [dropTargetNode, setDropTargetNode] =
    useState<FlowChartContext['dropTargetNode']>(null);
  const domEl = useStore(domElSelector);
  const flowBounds = useMeasureDOMElement(domEl);

  return {
    editable,
    allowGrouping,
    isDirty,
    dropTargetNode,
    flowBounds,
    __internal: {
      setDropTargetNode,
      setIsDirty,
      markers,
      setMarkers,
    },
  };
}

export const FlowChartContextProvider = ({
  children,
  ...props
}: PropsWithChildren<EditingProps>) => {
  const value = useFlowChartContextValue(props);
  return (
    <FlowChartContext.Provider value={value}>
      <LiveEdgeCrossingsProvider>{children}</LiveEdgeCrossingsProvider>
    </FlowChartContext.Provider>
  );
};

export const FlowChartProvider = ({
  children,
  editable,
  allowGrouping,
  ...props
}: Parameters<typeof ReactFlowProvider>[0] & EditingProps) => {
  return (
    <ReactFlowProvider {...props}>
      <FlowChartContextProvider
        editable={editable}
        allowGrouping={allowGrouping}
      >
        {children}
      </FlowChartContextProvider>
    </ReactFlowProvider>
  );
};
